import React, { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";

import BookingForm from "./components/BookingForm";
import ContactButton from "./components/ContactButton";
import ErrorBoundary from "./components/ErrorBoundary";
import FooterText from "./components/FooterText";
import List from "./components/List";
import LoaderScreen from "./components/LoaderScreen";
import MenuList from "./components/MenuList";
import Video from "./components/Video";
import { debounce, scrollStartStop } from "./services/utils";

const video = new URL("./assets/video/video_new.mp4", import.meta.url);
const video1 = new URL("./assets/video/video1.mp4", import.meta.url);
const video2 = new URL("./assets/video/video2.mp4", import.meta.url);
const video3 = new URL("./assets/video/video3.mp4", import.meta.url);
const video4 = new URL("./assets/video/video4.mp4", import.meta.url);

const videoThumb = new URL(
  "./assets/images/thumbnail_fin.jpg",
  import.meta.url
);
const video1Thumb = new URL("./assets/images/video1.jpg", import.meta.url);
const video2Thumb = new URL("./assets/images/video2.jpg", import.meta.url);
const video3Thumb = new URL("./assets/images/video3.jpg", import.meta.url);
const video4Thumb = new URL("./assets/images/video4.jpg", import.meta.url);

const iconChurch = new URL("./assets/images/icon-church.svg", import.meta.url);
const iconUrn = new URL("./assets/images/icon-urn.svg", import.meta.url);
const iconBook = new URL("./assets/images/icon-book.svg", import.meta.url);
const iconBird = new URL("./assets/images/icon-bird.svg", import.meta.url);

const defaultContactButtonPosition = 180;

export default function App() {
  const [contactButtonPosition, setContactButtonPosition] = useState(
    defaultContactButtonPosition
  );
  const [contactButtonVisibility, setContactButtonVisibility] = useState(true);
  const [loading, setLoading] = useState(true);
  const [innerHeight, setInnerHeight] = useState(window.innerHeight);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [cookies, setCookie] = useCookies(["app"]);

  useEffect(() => {
    if (window.innerWidth > 600) {
      window.location.href =
        "https://fenixbegravning.se/begravningsbyra/stockholm/";
      return;
    }
    setLoading(false);

    const dt = new Date();
    dt.setDate(dt.getDate() + 30);
    setCookie("fnx_opt_mob_city", "stockholm", {
      path: "/",
      expires: dt,
    });
  }, []);

  useEffect(() => {
    const debouncedHandleScroll = debounce(function handleResize() {
      setContactButtonPosition(() =>
        window.scrollY <= 20 ? defaultContactButtonPosition : 20
      );
    }, 10);
    window.addEventListener("scroll", debouncedHandleScroll);
    return () => window.removeEventListener("scroll", debouncedHandleScroll);
  }, []);

  useEffect(() => {
    scrollStartStop(
      () => {
        setContactButtonVisibility(false);
      },
      () => {
        setContactButtonVisibility(true);
      },
      500
    );
  }, []);

  if (loading === true) {
    return <LoaderScreen />;
  }

  return (
    <ErrorBoundary>
      <ContactButton
        position={contactButtonPosition}
        positionDefault={defaultContactButtonPosition}
        visible={contactButtonVisibility}
        showBookingForm={setShowBookingForm}
      />
      <Video
        height={innerHeight}
        video={video}
        thumb={videoThumb}
        isHeroVideo={true}
        showBookingForm={setShowBookingForm}
        loadedDelay={750}
      />
      <div className={`py-[30px] px-6 text-center relative overflow-hidden`}>
        <div className="bg-flower bg-no-repeat h-full w-full absolute left-[-30px] top-[110px] z-0"></div>
        <div className="uppercase text-[26px] text-dark-blue tracking-[4px] leading-[30px] font-bold mb-4">
          välkommen
          <br />
          hem till oss
        </div>
        <div className="font-interlight mb-3">
          Fenix är en rikstäckande
          <br />
          begravningsbyrå som hjälpt
        </div>
        <div className="font-intermedium text-[#E9E3D0] text-[52px] leading-none mb-3">
          8000+
        </div>
        <div className="font-interlight">
          familjer i hela Sverige att ta farväl.
        </div>
        <hr className="my-7 border-dark-blue" />
        <div className="font-intermedium text-[30px] leading-none mb-4">
          Låt oss ta hand om dig
        </div>
        <div className="font-interlight text-[18px]">
          – Vi avlastar dig hela vägen,
          <br />
          tryggt och säkert:
        </div>
      </div>
      <List />
      <div className="py-[30px] px-10">
        <div className="uppercase text-[26px] text-dark-blue tracking-[4px] leading-[30px] font-bold text-center">
          Fenix begravnings-
          <br />
          erbjudande:
        </div>
      </div>
      <Video video={video1} thumb={video1Thumb} showMuteButton={false}>
        <div className="absolute bottom-0 w-full z-20 bg-gradient-to-b from-transparent via-transparent to-black">
          <div
            className="relative bottom-1 text-white text-center pb-20 px-10"
            onClick={() => {
              window.location.href =
                "https://fenixbegravning.se/begravning-med-ceremoni/stockholm/";
            }}
          >
            <img src={iconChurch} alt="" className="mb-4 mx-auto" />
            <div className="uppercase text-[26px] leading-[30px] tracking-[4px] font-bold mb-4">
              begravning med ceremoni
            </div>
            <div className="mb-[-20px]">
              En minnesvärd och personlig ceremoni för närstående att samlas,
              hedra minnet och ta ett sista farväl.
            </div>
          </div>
        </div>
      </Video>
      <Video video={video2} thumb={video2Thumb} showMuteButton={false}>
        <div className="absolute bottom-0 w-full z-20 bg-gradient-to-b from-transparent via-transparent to-black">
          <div
            className="relative bottom-1 text-white text-center pb-20 px-10"
            onClick={() => {
              window.location.href =
                "https://fenixbegravning.se/direktkremering/stockholm/";
            }}
          >
            <img src={iconUrn} alt="" className="mb-4 mx-auto" />
            <div className="uppercase text-[26px] leading-[30px] tracking-[4px] font-bold mb-4">
              direktkremation
            </div>
            <div className="mb-[-20px]">
              Begravning utan ceremoni är ett sätt för nära och kära att ta
              avsked och visa sin kärlek på ett värdigt, men enkelt sätt.
            </div>
          </div>
        </div>
      </Video>
      <Video video={video3} thumb={video3Thumb} showMuteButton={false}>
        <div className="absolute bottom-0 w-full z-20 bg-gradient-to-b from-transparent via-transparent to-black">
          <div
            className="relative bottom-1 text-white text-center pb-20 px-10"
            onClick={() => {
              window.location.href =
                "https://fenixbegravning.se/begravning-med-ceremoni-bouppteckning/stockholm/";
            }}
          >
            <img src={iconBook} alt="" className="mb-4 mx-auto" />
            <div className="uppercase text-[26px] leading-[30px] tracking-[4px] font-bold mb-4">
              Begravning med ceremoni & bouppteckning
            </div>
            <div className="mb-[-20px]">
              En vacker och minnesvärd ceremoni för ett sista farväl samt
              juridisk hjälp med sammanställningen av dödsboets tillgångar och
              skulder.
            </div>
          </div>
        </div>
      </Video>
      <Video video={video4} thumb={video4Thumb} showMuteButton={false}>
        <div className="absolute bottom-0 w-full z-20 bg-gradient-to-b from-transparent via-transparent to-black">
          <div
            className="relative bottom-1 text-white text-center pb-20 px-10"
            onClick={() => {
              window.location.href =
                "https://fenixbegravning.se/anpassad-begravning/stockholm/";
            }}
          >
            <img src={iconBird} alt="" className="mb-4 mx-auto" />
            <div className="uppercase text-[26px] leading-[30px] tracking-[4px] font-bold mb-4">
              anpassad begravning
            </div>
            <div className="mb-[-20px]">
              En minnesvärd och stämningsfull ceremoni helt utformad efter era
              önskemål för ett personligt avsked.
            </div>
          </div>
        </div>
      </Video>
      <MenuList />
      <div className="py-[20px] pb-[120px] px-5 text-dark-blue ">
        <FooterText />
        <div className="flex justify-between text-[9px] tracking-[4px] uppercase mt-5">
          <a href="https://fenixbegravning.se/wp-content/uploads/2020/10/Allmanna-villkor-2020-10-06.pdf">
            allmänna villkor
          </a>
          <a href="https://fenixbegravning.se/wp-content/uploads/2022/01/fenix_integritetspolicy_kund_uppdaterad_2022-01-26.pdf">
            integritetspolicy
          </a>
        </div>
      </div>
      {showBookingForm && <BookingForm closeForm={setShowBookingForm} />}
    </ErrorBoundary>
  );
}
